// BasketballGameDashboard.jsx
import React, { useMemo, useEffect, useRef, useState } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TableHead,
} from "@material-ui/core";
import { getContrastingColor } from "../../../../services/utils";

const formatTimeMMSS = (totalSeconds) => {
    const safe = Math.max(0, Number(totalSeconds) || 0);
    const m = Math.floor(safe / 60)
        .toString()
        .padStart(2, "0");
    const s = Math.floor(safe % 60)
        .toString()
        .padStart(2, "0");
    return `${m}:${s}`;
};

/**
 * props:
 *  - game: documento original (con estado.teamAInfo, estado.teamBInfo)
 *  - summary: objeto de stats (currentPeriod, A, B, etc.)
 *  - clock: { tiempoSegundos: number, runningClock: boolean, timestampClock?: number }
 *
 * NOTA: En esta vista NO usamos timestampClock por posible desincronización de hora.
 *       Cada update "resetea" displaySeconds y si runningClock es true decrece localmente 1/seg.
 */
const BasketballGameDashboard = ({ game, summary, clock }) => {
    const { estado } = game || {};
    const { teamAInfo = {}, teamBInfo = {} } = estado || {};

    const statsA = summary?.A;
    const statsB = summary?.B;

    // Siempre 4 periodos
    const periods = [1, 2, 3, 4];

    // Totales de puntos por equipo
    const teamATotalPoints = statsA?.totalPuntos ?? 0;
    const teamBTotalPoints = statsB?.totalPuntos ?? 0;

    // Puntos / faltas por periodo
    const getPointsByPeriod = (teamStats, period) => {
        const v = teamStats?.porCuarto?.[period];
        return v === undefined || v === null ? "" : v;
    };

    const getFoulsByPeriod = (teamStats, period) => {
        const v = teamStats?.faltasPorcuarto?.[period];
        return v === undefined || v === null ? "" : v;
    };

    // Lista de jugadores por equipo desde summary (ignoramos el slot "team")
    const playersA = useMemo(
        () =>
            Object.values(statsA?.players || {}).filter((p) => p.number !== "team"),
        [statsA]
    );

    const playersB = useMemo(
        () =>
            Object.values(statsB?.players || {}).filter((p) => p.number !== "team"),
        [statsB]
    );

    const currentPeriod = summary?.currentPeriod ?? estado?.currentPeriod ?? 1;

    // =========================
    // ✅ RELOJ PUBLICO (cuenta regresiva local)
    // =========================
    const [displaySeconds, setDisplaySeconds] = useState(() =>
        Math.max(0, Number(clock?.tiempoSegundos) || 0)
    );
    const tickRef = useRef(null);

    const stopTick = () => {
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    };

    const startTick = () => {
        stopTick();
        tickRef.current = setInterval(() => {
            setDisplaySeconds((prev) => Math.max(0, (Number(prev) || 0) - 1));
        }, 1000);
    };

    // Cada vez que cambie clock => resincroniza
    useEffect(() => {
        const base = Math.max(0, Number(clock?.tiempoSegundos) || 0);
        setDisplaySeconds(base);

        if (clock?.runningClock) startTick();
        else stopTick();

        return () => stopTick();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clock?.tiempoSegundos, clock?.runningClock]);

    // Cleanup al desmontar
    useEffect(() => {
        return () => stopTick();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // =========================

    const miniCell = (color) => ({
        padding: "4px 4px",
        fontSize: 12,
        lineHeight: 1.1,
        color,
        borderBottom: "none",
        width: 22,
        textAlign: "center",
    });

    const miniLabelCell = (color) => ({
        padding: "4px 4px",
        fontSize: 15,
        lineHeight: 1.1,
        fontWeight: 700,
        color,
        borderBottom: "none",
        width: 34,
        whiteSpace: "nowrap",
    });

    const renderMiniTableForTeam = (side, teamInfo) => {
        const teamStats = side === "A" ? statsA : statsB;
        const contrast = getContrastingColor(teamInfo?.teamColor);

        return (
            <Table
                size="small"
                style={{
                    tableLayout: "fixed",
                    margin: "1 auto",
                }}
            >
                <TableBody>
                    {/* Encabezado: periodos */}
                    <TableRow>
                        <TableCell align="center" style={miniLabelCell(contrast)} />
                        {periods.map((p) => (
                            <TableCell key={p} align="center" style={miniCell(contrast)}>
                                {p}
                            </TableCell>
                        ))}
                    </TableRow>

                    {/* Fila de puntos */}
                    <TableRow>
                        <TableCell align="center" style={miniLabelCell(contrast)}>
                            Puntos
                        </TableCell>
                        {periods.map((p) => (
                            <TableCell key={p} align="center" style={miniCell(contrast)}>
                                {getPointsByPeriod(teamStats, p) || 0}
                            </TableCell>
                        ))}
                    </TableRow>

                    {/* Fila de faltas */}
                    <TableRow>
                        <TableCell align="center" style={miniLabelCell(contrast)}>
                            Faltas
                        </TableCell>
                        {periods.map((p) => (
                            <TableCell key={p} align="center" style={miniCell(contrast)}>
                                {getFoulsByPeriod(teamStats, p) || 0}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableBody>
            </Table>
        );
    };

    const playersTable = { padding: "8px 4px" };

    const renderPlayersTable = (players, teamName, team) => (
        <Paper>
            <Box p={2}>

                <Typography variant="h6" gutterBottom style={{
                    padding: 10,
                    borderRadius: 5,
                    boxShadow: 10,
                    backgroundColor: team === "A" ? teamAColor : teamBColor,
                    color: getContrastingColor(team === "A" ? teamAColor : teamBColor),
                }}>
                    {teamName} — Jugadores
                </Typography>

                <Table size="small" style={{ padding: "2px" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell style={playersTable} align="center">
                                Núm
                            </TableCell>
                            <TableCell style={playersTable} align="center">
                                Nombre
                            </TableCell>
                            <TableCell style={playersTable} align="center">
                                Puntos
                            </TableCell>
                            <TableCell style={playersTable} align="center">
                                Faltas
                            </TableCell>
                            <TableCell style={playersTable} align="center">
                                +1
                            </TableCell>
                            <TableCell style={playersTable} align="center">
                                +2
                            </TableCell>
                            <TableCell style={playersTable} align="center">
                                +3
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {players.map((player) => (
                            <TableRow key={player.number}>
                                <TableCell style={playersTable} align="center">
                                    {player.number}
                                </TableCell>
                                <TableCell style={playersTable} align="center">
                                    {player.name}
                                </TableCell>
                                <TableCell style={playersTable} align="center">
                                    {player.totalPuntos ?? 0}
                                </TableCell>
                                <TableCell style={playersTable} align="center">
                                    {player.totalFaltas ?? 0}
                                </TableCell>
                                <TableCell style={playersTable} align="center">
                                    {player.mas1 ?? 0}
                                </TableCell>
                                <TableCell style={playersTable} align="center">
                                    {player.mas2 ?? 0}
                                </TableCell>
                                <TableCell style={playersTable} align="center">
                                    {player.mas3 ?? 0}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </Paper>
    );

    const teamAColor = teamAInfo?.teamColor || "#ccc";
    const teamBColor = teamBInfo?.teamColor || "#ccc";

    return (
        <Box p={2}>
            {/* RELOJ ARRIBA, CENTRADO */}
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Paper style={{ padding: 10, textAlign: "center" }}>
                        <Typography variant="h5">Cuarto Nro: {currentPeriod}</Typography>

                        <Typography variant="h2" style={{ marginTop: 5, fontFamily: "Digital", }}>
                            {formatTimeMMSS(displaySeconds)}
                        </Typography>

                        {/* <Typography variant="subtitle1" style={{ opacity: 0.8, }}>
                            {clock?.runningClock ? "Corriendo" : "Pausado"}
                        </Typography> */}
                    </Paper>
                </Grid>

                {/* SCORES */}
                <Grid item xs={6} md={6}>
                    <Paper
                        style={{
                            padding: 12,
                            backgroundColor: teamAColor,
                            color: getContrastingColor(teamAColor),
                            height: "100%",
                        }}
                    >
                        <Typography variant="h5" align="center">
                            {teamAInfo?.name || "Equipo A"}
                        </Typography>
                        <Typography variant="h1" align="center">
                            {teamATotalPoints}
                        </Typography>

                        <Box
                            mt={2}
                            style={{
                                backgroundColor: "rgba(0,0,0,0.15)",
                                borderRadius: 8,
                                overflow: "hidden",
                            }}
                        >
                            {renderMiniTableForTeam("A", teamAInfo)}
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={6} md={6}>
                    <Paper
                        style={{
                            padding: 12,
                            backgroundColor: teamBColor,
                            color: getContrastingColor(teamBColor),
                            height: "100%",
                        }}
                    >
                        <Typography variant="h5" align="center">
                            {teamBInfo?.name || "Equipo B"}
                        </Typography>
                        <Typography variant="h1" align="center">
                            {teamBTotalPoints}
                        </Typography>

                        <Box
                            mt={2}
                            style={{
                                backgroundColor: "rgba(0,0,0,0.15)",
                                borderRadius: 8,
                                overflow: "hidden",
                            }}
                        >
                            {renderMiniTableForTeam("B", teamBInfo)}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* TABLAS DE JUGADORES */}
            <Box mt={3}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        {renderPlayersTable(playersA, teamAInfo?.name || "Equipo A", "A")}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {renderPlayersTable(playersB, teamBInfo?.name || "Equipo B", "B")}
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default BasketballGameDashboard;
